import APIService from '../../../Services/APIService';
import { Apis } from '../../../Services/app.constants';
import { formatReportingDate } from '../constants/reportingConfig';

function buildScopeQuery(scope = {}) {
  const params = new URLSearchParams();
  if (scope.company_id) params.set('company_id', String(scope.company_id));
  if (scope.bot_number) params.set('bot_number', String(scope.bot_number));
  if (scope.template_id) params.set('template_id', String(scope.template_id));
  if (scope.start_date) params.set('start_date', formatReportingDate(scope.start_date));
  if (scope.end_date) params.set('end_date', formatReportingDate(scope.end_date));
  if (scope.sent_date) params.set('sent_date', formatReportingDate(scope.sent_date));
  return params.toString();
}

function unwrapPayload(res) {
  if (res?.status !== 200) {
    throw new Error('Request failed');
  }
  const body = res?.data;
  if (body?.status === false) {
    const err =
      body?.data?.general_error ||
      (typeof body?.data === 'string' ? body.data : null) ||
      'Request failed';
    throw new Error(err);
  }
  if (body?.status === true) return body.data;
  return body?.data ?? body;
}

export const templateReportingService = {
  buildScopeQuery,

  async getOverview(scope = {}) {
    const query = buildScopeQuery(scope);
    const res = await APIService.get(`${Apis.whatsappReportingOverview}?${query}`);
    return unwrapPayload(res);
  },

  async getTemplates(scope = {}) {
    const query = buildScopeQuery(scope);
    const res = await APIService.get(`${Apis.whatsappReportingTemplates}?${query}`);
    const payload = unwrapPayload(res);
    return payload?.results ?? [];
  },

  async getTemplateDetail(templateId, scope = {}) {
    const query = buildScopeQuery(scope);
    const res = await APIService.get(
      `${Apis.whatsappReportingTemplateDetail}${encodeURIComponent(templateId)}/?${query}`
    );
    return unwrapPayload(res);
  },

  async getTemplateDaily(templateId, scope = {}, pagination = {}) {
    const params = new URLSearchParams(buildScopeQuery(scope));
    params.set('page', String(pagination.page ?? 0));
    params.set('limit', String(pagination.limit ?? 30));
    const res = await APIService.get(
      `${Apis.whatsappReportingTemplateDaily}${encodeURIComponent(templateId)}/daily/?${params}`
    );
    const payload = unwrapPayload(res);
    return {
      results: payload?.results ?? [],
      page: payload?.page ?? 0,
      limit: payload?.limit ?? 30,
    };
  },

  async getCta(scope = {}) {
    const query = buildScopeQuery(scope);
    const res = await APIService.get(`${Apis.whatsappReportingCta}?${query}`);
    const payload = unwrapPayload(res);
    return payload?.results ?? [];
  },

  async getFailures(scope = {}) {
    const query = buildScopeQuery(scope);
    const res = await APIService.get(`${Apis.whatsappReportingFailures}?${query}`);
    const payload = unwrapPayload(res);
    return payload?.results ?? [];
  },

  async exportCsv(scope = {}, fileName) {
    const query = buildScopeQuery(scope);
    const start = formatReportingDate(scope.start_date) || 'start';
    const end = formatReportingDate(scope.end_date) || 'end';
    return APIService.downloadFile(
      `${Apis.whatsappReportingExport}?${query}`,
      fileName || `template_reports_${start}_${end}.csv`
    );
  },

  async syncReporting(scope = {}) {
    const res = await APIService.post(Apis.whatsappReportingSync, {
      company_id: scope.company_id ? String(scope.company_id) : undefined,
      bot_number: scope.bot_number ? String(scope.bot_number) : undefined,
      template_id: scope.template_id ? String(scope.template_id) : undefined,
      start_date: scope.start_date ? formatReportingDate(scope.start_date) : undefined,
      end_date: scope.end_date ? formatReportingDate(scope.end_date) : undefined,
    });
    return unwrapPayload(res);
  },

  async getRecipients(templateId, scope = {}, pagination = {}, filters = {}) {
    const params = new URLSearchParams(buildScopeQuery(scope));
    params.set('page', String(pagination.page ?? 0));
    params.set('limit', String(pagination.limit ?? 20));
    if (filters.status) params.set('status', String(filters.status));
    if (filters.search) params.set('search', String(filters.search));
    const res = await APIService.get(
      `${Apis.whatsappReportingRecipients}${encodeURIComponent(templateId)}/recipients/?${params}`
    );
    const payload = unwrapPayload(res);
    return {
      results: payload?.results ?? [],
      total_count: payload?.total_count ?? 0,
      page: payload?.page ?? 0,
      limit: payload?.limit ?? 20,
    };
  },

  async exportRecipients(templateId, scope = {}, fileName, filters = {}) {
    const params = new URLSearchParams(buildScopeQuery(scope));
    if (filters.status) params.set('status', String(filters.status));
    if (filters.search) params.set('search', String(filters.search));
    const sentDate = formatReportingDate(scope.sent_date) || 'export';
    return APIService.downloadFile(
      `${Apis.whatsappReportingRecipientsExport}${encodeURIComponent(templateId)}/recipients/export/?${params}`,
      fileName || `template_recipients_${sentDate}.csv`
    );
  },

  async updateRecipientCrm(templateId, messageId, payload = {}) {
    const res = await APIService.post(
      `${Apis.whatsappReportingRecipientsCrm}${encodeURIComponent(templateId)}/recipients/crm/`,
      { message_id: messageId, ...payload }
    );
    return unwrapPayload(res);
  },
};
